import { Button, Dropdown, Menu, message, Tooltip } from "antd";
import { MoreOutlined, SettingOutlined } from "@ant-design/icons";
import { useState } from "react";
import styled from "styled-components";
import {
  RollFormat,
  RollSessionFieldsFragment,
  TimesheetRange,
  Week,
  WeekRollFieldsFragment,
} from "../../../../../generated/graphql";
import {
  ABSENT_COMMENTS,
  formatSessionDate,
  formatSessionTimes,
  getRollEntry,
  isFutureSession,
  isInvalidPresentComment,
} from "./rollUtils";
import useRollMutations from "./useRollMutations";
import WeekSessionsEditor, {
  RollAttendance,
  RollClassInfo,
} from "./WeekSessionsEditor";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  margin-top: 6px;
  font-size: 11px;
  font-weight: normal;
  line-height: 16px;
`;

const SessionDateLine = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  font-weight: 500;
`;

const SessionTimes = styled.div`
  white-space: nowrap;
  color: rgba(0, 0, 0, 0.55);
`;

const MenuIcon = styled(MoreOutlined)`
  cursor: pointer;
  padding: 2px;
  border-radius: 2px;
  &:hover {
    background-color: rgba(0, 0, 0, 0.06);
  }
`;

const LegacyLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  color: rgba(0, 0, 0, 0.55);
`;

const LinkButton = styled(Button)`
  height: auto;
  padding: 0;
  font-size: 11px;
`;

/**
 * Shows the sessions of a class in a week in the heading of the week's column, with
 * actions to mark the roll for the whole class and edit the sessions.
 */
const WeekSessionsHeader: React.FC<{
  classId: number;
  week: Week;
  weekRoll?: WeekRollFieldsFragment;
  attendances: RollAttendance[];
  classInfo: RollClassInfo;
  timesheetRange?: TimesheetRange;
  isEditable: boolean;
}> = ({
  classId,
  week,
  weekRoll,
  attendances,
  classInfo,
  timesheetRange,
  isEditable,
}) => {
  const [isEditorVisible, setIsEditorVisible] = useState(false);
  const { setRollEntries } = useRollMutations();

  if (!weekRoll) {
    return null;
  }

  const markAll = async (
    session: RollSessionFieldsFragment,
    isPresent: boolean,
    reason?: string
  ) => {
    const entries = attendances
      .filter((attendance) => !attendance.isDeleted)
      .map((attendance) => {
        let comment =
          reason ??
          getRollEntry(weekRoll, session.key, attendance.id)?.comment ??
          "";
        if (isPresent && isInvalidPresentComment(comment)) {
          comment = "";
        }
        return { attendanceId: attendance.id, comment, isPresent };
      });

    try {
      await setRollEntries({
        classId,
        entries,
        sessionKey: session.key,
        week,
      });
      message.success("Automatically saved for entire class");
    } catch (error) {
      message.error(
        `Automatic save for entire class failed: ${error.message}`,
        10
      );
    }
  };

  const sessionMenu = (session: RollSessionFieldsFragment) => (
    <Menu>
      <Menu.Item
        key="present"
        disabled={isFutureSession(session)}
        onClick={() => markAll(session, true)}
      >
        Mark everyone present
      </Menu.Item>
      <Menu.Item key="absent" onClick={() => markAll(session, false)}>
        Mark everyone absent
      </Menu.Item>
      <Menu.SubMenu key="reason" title="Mark everyone absent with reason">
        {ABSENT_COMMENTS.map((reason) => (
          <Menu.Item
            key={reason}
            onClick={() => markAll(session, false, reason)}
          >
            {reason}
          </Menu.Item>
        ))}
      </Menu.SubMenu>
    </Menu>
  );

  const editor = (
    <WeekSessionsEditor
      isVisible={isEditorVisible}
      onClose={() => setIsEditorVisible(false)}
      classId={classId}
      week={week}
      weekRoll={weekRoll}
      attendances={attendances}
      classInfo={classInfo}
      timesheetRange={timesheetRange}
    />
  );

  if (weekRoll.format === RollFormat.Legacy) {
    // Only prompt to switch weeks recorded in the old format which can be edited.
    return isEditable ? (
      <Container>
        {editor}
        <LegacyLine>
          Old format
          <Tooltip title="This week was recorded in the old format. Switch it to the new roll to make changes.">
            <LinkButton type="link" onClick={() => setIsEditorVisible(true)}>
              Switch
            </LinkButton>
          </Tooltip>
        </LegacyLine>
      </Container>
    ) : null;
  }

  if (!isEditable && weekRoll.format === RollFormat.LegacyEmpty) {
    return null;
  }

  return (
    <Container>
      {editor}
      {weekRoll.sessions.length === 0 && (
        <SessionTimes>
          {weekRoll.format === RollFormat.Sessions
            ? "No class this week"
            : // Holiday weeks (and Wk0) have no session until the teacher adds one.
              "No sessions"}
        </SessionTimes>
      )}
      {weekRoll.sessions.map((session) => (
        <div key={session.key}>
          <SessionDateLine>
            {formatSessionDate(session)}
            {isEditable && (
              <Dropdown overlay={sessionMenu(session)} trigger={["click"]}>
                <MenuIcon />
              </Dropdown>
            )}
          </SessionDateLine>
          <SessionTimes>{formatSessionTimes(session)}</SessionTimes>
        </div>
      ))}
      {isEditable && (
        <Tooltip title="Change the dates and times the class ran this week">
          <Button
            size="small"
            icon={<SettingOutlined />}
            onClick={() => setIsEditorVisible(true)}
          >
            Sessions
          </Button>
        </Tooltip>
      )}
    </Container>
  );
};

export default WeekSessionsHeader;
